import React, { useState } from "react"
import {
	Grid,
	Typography,
	Link,
	TextField,
	Chip
} from "@material-ui/core"
import { Link2 as Link2Icon } from "lucide-react"

import { themeColors } from "letalkui"

import {
	Divider,
	ActionDialog,
	InfoCard,
	Loading,
	Notification
} from "@/components"

import { renderComponent } from "@/utils/node"
import { isCustomWebhook } from "@/utils/webhook"
import { letalkLinks, whatsappLinks } from "@/utils/link"

import useDidMount from "@/hooks/useDidMount"
import useValidation, { ErrorType } from "@/hooks/useValidation"
import useSubscriptionLimits from "@/hooks/useSubscriptionLimits"

import ApiService from "@/services/Api"
import ErrorHandlerService from "@/services/ErrorHandler"

import { usePermissionStateStore } from "@/store/PermissionState"
import { PERMISSION_DENIED_MESSAGE } from "@/components/PermissionGate/BasePermissionGate"

import { Webhook, WebhookCustomFieldAssociation, WebhookDefaultFields, WebhookType } from "@/protocols/webhook"
import { CustomField } from "@/protocols/customField"

import WebhookFieldAssociationInput from "@/pages/Admin/Webhooks/WebhookFieldAssociationInput"
import WebhookCustomFieldAssociationInput from "@/pages/Admin/Webhooks/WebhookCustomFieldAssociationInput"

import WebhookFieldAssociationEditorDialogSkeleton from "@/skeletons/Admin/WebhookFieldAssociationEditorDialogSkeleton"

import useStyles from "@/pages/Admin/Webhooks/WebhookFieldAssociationEditorDialog/styles"
import { colors } from "@/styles/colors"
import ColorUtil from "@/utils/ColorUtil"

type WebhookFieldAssociationEditorPermissionAction =
	| { feature: "integration"; action: "create" }
	| { feature: "chat-bot"; action: "update" }

type WebhookFieldAssociationEditorDialogProps = {
	webhookId: number
	webhookType: WebhookType
	permissionAction?: WebhookFieldAssociationEditorPermissionAction
	saveText?: string
	onSave?: () => void
	onClose?: () => void
}

type WebhookFieldAssociationEditorDialogType = {
	open: (props: WebhookFieldAssociationEditorDialogProps) => void
}

type DefaultFieldsAssociationInput = {
	key: keyof WebhookDefaultFields
	label: React.ReactNode
	placeholder?: string
}

const defaultFieldsAssociationInputs: DefaultFieldsAssociationInput[] = [
	{
		key: "firstName",
		label: "Nome",
		placeholder: "contacts.name"
	},
	{
		key: "email",
		label: "E-mail",
		placeholder: "contacts.email"
	},
	{
		key: "phone",
		label: (
			<>
				Telefone
				<Chip
					size="small"
					label="Obrigatório"
					style={{
						...ColorUtil.getBackgroundAndContrastColor(colors.yellow[400]),
						marginLeft: "8px"
					}}
				/>
			</>
		),
		placeholder: "contacts.phone"
	},
	{
		key: "contactValue",
		label: "Valor do contato",
		placeholder: "contacts.value"
	}
]

const WebhookFieldAssociationEditorDialog: WebhookFieldAssociationEditorDialogType & React.FC<WebhookFieldAssociationEditorDialogProps> = (props) => {
	const {
		webhookId,
		webhookType,
		permissionAction,
		onSave,
		onClose,
		saveText
	} = props

	const [opened, setOpened] = useState(true)
	const [saving, setSaving] = useState(false)
	const [loading, setLoading] = useState(true)

	const [defaultFieldsAssociation, setDefaultFieldsAssociation] = useState({} as WebhookDefaultFields)
	const [customFieldsAssociation, setCustomFieldsAssociation] = useState<WebhookCustomFieldAssociation[]>([])
	const [customFields, setCustomFields] = useState<CustomField[]>([])

	const classes = useStyles()
	const { triggerValidation, clearValidation, validation } = useValidation()
	const { checkPermission } = usePermissionStateStore()

	const funnelSubscriptionData = useSubscriptionLimits("permission", "funnel")
	const canUseContactValue = !funnelSubscriptionData.mustBlock

	const hasPermissionToSave = permissionAction
		? checkPermission(permissionAction.feature, permissionAction.action)
		: true
	const isSaveButtonDisabled = !hasPermissionToSave

	const isCustom = isCustomWebhook(webhookType)

	/**
	 * "Valor do contato" is gated by the funnel subscription and is the only
	 * default field available for non-custom webhooks (Active Campaign, RD
	 * Station, HubSpot). Nome/E-mail/Telefone remain exclusive to the custom
	 * webhook.
	 */
	const visibleDefaultFieldsInputs = defaultFieldsAssociationInputs.filter(input => {
		if (input.key === "contactValue") {
			return canUseContactValue
		}

		return isCustom
	})

	const handleChangeCustomFieldsAssociation = (data: WebhookCustomFieldAssociation[]) => {
		setCustomFieldsAssociation(data)
	}

	const handleChangeDefaultFieldsAssociation = (key: keyof WebhookDefaultFields, value: string) => {
		setDefaultFieldsAssociation(lastState => ({
			...lastState,
			/**
			 * Sometimes user just copy/paste the json_path from somewhere and forgets an empty space before or after the json_path
			 * Since its very hard for the user to see the blank space before/after the json_path, we are removing it
			 */
			[key]: value?.trim()
		}))

		clearValidation(key)
	}

	const handleClose = () => {
		setOpened(false)

		onClose?.()
	}

	const handleSave = async () => {
		setSaving(true)

		const shouldSendDefaultFieldsAssociation = isCustom || canUseContactValue

		try {
			await ApiService.put(`/webhooks/${webhookId}/fields-association`, {
				...(shouldSendDefaultFieldsAssociation ? { defaultFieldsAssociation } : {}),
				customFieldsAssociation
			})

			Notification.success({
				message: "Os dados de associação do webhook foram alterados com sucesso!"
			})

			setOpened(false)
			onSave?.()
		} catch (error) {
			triggerValidation(error as ErrorType)
		}

		setSaving(false)
	}

	const loadAssociations = async () => {
		try {
			const result = await ApiService.get("/webhooks", {
				params: {
					webhookId
				}
			})
			const webhook: Webhook = result.data

			setDefaultFieldsAssociation((webhook?.default_fields_association || {}) as WebhookDefaultFields)
			setCustomFieldsAssociation(webhook?.custom_fields_association || [])
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
		}
	}

	const loadCustomFields = async () => {
		try {
			const result = await ApiService.get("/custom-fields")

			const customFields: CustomField[] = result.data.customFields

			if (customFields) {
				setCustomFields(customFields)
			}
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
		}
	}

	useDidMount(async () => {
		await Promise.all([
			loadAssociations(),
			loadCustomFields()
		])

		setLoading(false)
	})

	return (
		<ActionDialog
			title={isCustomWebhook(webhookType) ? "WEBHOOK CUSTOMIZÁVEL" : "WEBHOOK - SALVAR INFORMAÇÕES NO CONTATO"}
			openDialog={opened}
			saveText={saveText || "FECHAR"}
			fullWidth={true}
			onSave={handleSave}
			onClose={handleClose}
			loading={saving}
			saveButtonDisabled={isSaveButtonDisabled}
			saveButtonDisabledTooltip={isSaveButtonDisabled ? PERMISSION_DENIED_MESSAGE : undefined}
			hideCloseButton={true}
			maxWidth="md"
		>
			<Grid
				container
				alignContent="center"
			>
				<Grid
					item
					xs={12}
				>
					<Typography
						variant="body1"
						color="textPrimary"
					>
						{isCustomWebhook(webhookType) ? (
							"Associe os campos da Letalk com os campos da estrutura de dados recebidos via Webhook."
						) : (
							"Para salvar informações recebidas via Webhook, adicione associações dos campos da estrutura de dados recebidos via Wehook com os campos personalizados criados na Letalk."
						)}
					</Typography>

					<Link
						href={letalkLinks.wikiHowToCreateCustomIntegrationChatbot}
						target="_blank"
					>
						Veja mais sobre como associar o pacote do Webhook com as informações da Letalk
					</Link>
				</Grid>

				<Divider size={5} orientation="horizontal" />

				<Grid
					item
					xs={12}
				>
					<Loading
						loading={loading}
						customLoadingElement={<WebhookFieldAssociationEditorDialogSkeleton />}
					>
						<Grid
							container
							spacing={6}
						>
							{visibleDefaultFieldsInputs.length > 0 && (
								<Grid
									item
									xs={12}
								>
									<WebhookFieldAssociationInput
										label={{
											leftSide: "Campos na Letalk",
											rightSide: "Dados que recebemos no pacote do Webhook"
										}}
										items={(
											visibleDefaultFieldsInputs.map(input => ({
												leftSide: (
													<Grid
														container
														alignItems="center"
														className={classes.associationDescription}
													>
														{input.label}
													</Grid>
												),
												rightSide: (
													<TextField
														placeholder={input.placeholder}
														variant="outlined"
														value={defaultFieldsAssociation[input.key]}
														onChange={({ target }) => handleChangeDefaultFieldsAssociation(input.key, target.value)}
														helperText={validation[input.key]}
														error={!!validation[input.key]}
														fullWidth
													/>
												)
											}))
										)}
									/>
								</Grid>
							)}

							<Grid
								item
								xs={12}
							>
								<WebhookCustomFieldAssociationInput
									customFieldsAssociaton={customFieldsAssociation}
									customFields={customFields}
									onChange={handleChangeCustomFieldsAssociation}
									validation={validation}
									clearValidation={clearValidation}
									loadCustomFields={loadCustomFields}
								/>
							</Grid>

							<Grid
								item
								xs={12}
							>
								<InfoCard
									icon={<Link2Icon size={20} color={themeColors.getPrimaryColor()} />}
									title="Precisa de um webhook fora do padrão?"
									description={(
										<>
											Se a estrutura de dados que você recebe não se encaixa nesses campos, nós fazemos a{" "}
											<strong>adaptação de webhooks fora do padrão</strong> — personalizamos os campos e
											parâmetros para falar exatamente a língua do seu sistema.
										</>
									)}
									ctaHref={whatsappLinks.sendMessageToLetalkPhone}
									ctaLabel="Falar com a Letalk →"
								/>
							</Grid>
						</Grid>
					</Loading>
				</Grid>
			</Grid>
		</ActionDialog>
	)
}

WebhookFieldAssociationEditorDialog.open = (props: WebhookFieldAssociationEditorDialogProps) => {
	renderComponent(
		"webhook-field-association-editor-dialog",
		<WebhookFieldAssociationEditorDialog
			{...props}
		/>
	)
}

export default WebhookFieldAssociationEditorDialog
